import React, { useEffect } from "react";
import { H1 } from "../../../components/utils/Headings";
import SaveInProgress from "../../../components/utils/SaveInProgress";
import { SideMenu } from "../../../components/utils/SideMenu/SideMenu.container";
import Spinner from "../../../components/utils/Spinner";
import SubTopbar from "../../../components/utils/SubTopbar";
import TopBar from "../../../components/utils/TopBar";
import EditableTitle from "../../Common/Components/EditableTitle";
import { ITabInformation, TabButtons, TabsContainer } from "../../Tabs";
import { addNewAdminPatch, modifyOrganizationTitlePatch, removeAdminPatch } from "./helpers/patches";
import Import from "./tabs/Import";
import Preview from "./tabs/Preview";
import Reports from "./tabs/Reports";
import Settings from "./tabs/Settings";
import { OrganizationSettingsProps, SettingsOption } from "./types";

const DEFAULT_TITLE = "Untitled Organisation Name";

const tabs: Array<ITabInformation<SettingsOption>> = [
  {
    queryParam: "preview",
    tabItem: SettingsOption.Preview,
    title: "Preview",
  },
  {
    queryParam: "settings",
    tabItem: SettingsOption.Settings,
    title: "Settings",
  },
  {
    queryParam: "import",
    tabItem: SettingsOption.Import,
    title: "Import",
  },
  {
    queryParam: "reports",
    tabItem: SettingsOption.Reports,
    title: "Reports",
  },
];

export const OrganizationSettingsComponent: React.FC<OrganizationSettingsProps> = props => {
  const [tab, setTab] = React.useState(props.initialTab);
  const setSelected = (selectedTab: SettingsOption) => () => setTab(selectedTab);
  const [menuExpanded, setMenuExpanded] = React.useState(true);
  const toggleMenuExpanded = () => setMenuExpanded(!menuExpanded);
  const renderPage = (children: React.ReactNode) => (
    <>
      <SaveInProgress firstFetch={props.firstFetch} inProgress={props.inProgress} />
      <TopBar showToggleMenu={true} onToggleMenuClick={toggleMenuExpanded} userName={props.userName} />
      <SideMenu
        expanded={menuExpanded}
        organizationName={props.organizationName}
        userName={props.userName}
        onClose={toggleMenuExpanded}
        isOpen={true}
      />
      <SubTopbar withSidebar={true} isOpen={true}>
        {children}
      </SubTopbar>
    </>
  );
  const patchAdmins = (action: "add" | "remove") => (adminId: string) => {
    const createPatch = action === "add" ? addNewAdminPatch : removeAdminPatch;
    props.patchRequest(createPatch(props.organization!, props.accountId, adminId));
  };

  const renderTab = () => {
    if (!props.organization) {
      return null;
    }

    switch (tab) {
      case SettingsOption.Preview:
        return <Preview />;
      case SettingsOption.Import:
        return (
          props.organization && (
            <Import
              orgId={props.organization.organizationId}
              accountId={props.accountId}
              uploadCsv={props.uploadCsv}
              uploadError={props.uploadError}
            />
          )
        );
      case SettingsOption.Reports:
        return <Reports accountId={props.accountId} organization={props.organization} />;
      default:
        return (
          <Settings
            admins={props.organizationAdmins}
            addNewAdmin={patchAdmins("add")}
            removeAdmin={patchAdmins("remove")}
            accountId={props.accountId}
            patchRequest={props.patchRequest}
            organization={props.organization}
          />
        );
    }
  };

  useEffect(() => {
    props.mount();
    return props.unmount;
  }, []);

  const modifyTitle = (title: string) => {
    if (title === props.organizationName || !props.organization) {
      return;
    }

    const patch = modifyOrganizationTitlePatch(props.organizationName, props.organization, props.accountId, title);
    props.patchRequest(patch);
  };

  if (!props.organization) {
    return <Spinner data-testid="spinner" />;
  }

  return renderPage(
    <>
      <H1>
        <EditableTitle
          addTitle={modifyTitle}
          modifyTitle={modifyTitle}
          currentTitle={props.organizationName}
          defaultTitle={DEFAULT_TITLE}
          removeTitle={true}
          isMainTitle={true}
        />
      </H1>
      <TabsContainer>
        <TabButtons tabs={tabs} displayed={tab} setSelected={setSelected} />
        {renderTab()}
      </TabsContainer>
    </>
  );
};
